import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { setFormat } from "state/orderEdition/actions";
import { Form, FormControl } from 'react-bootstrap';
import { toFrenchType } from "helper/globalHelper";

const FormatSelectComponent = ({orderIndex}) => {
  const dispatch = useDispatch();
  const order = useSelector((state) => state.orderEdition.orders[orderIndex]);
  const prestationType = order?.prestationType;
  const briqueMode = order?.briqMode ?? false;
  const formatTags = useSelector((state) => state.adminData.formatTags || []);
  const inactiveFormatTags = useSelector((state) => state.adminData.inactiveFormatTags || []);
  // En mode classique, on autorise aussi les formats historiques (inactifs).
  // En mode briques, seuls les 8 formats canoniques actifs sont proposés.
  const availableFormatTags = briqueMode ? formatTags : [...formatTags, ...inactiveFormatTags];
  const legacyFormats = useSelector((state) =>
    state.adminData.formats?.[prestationType] ? state.adminData.formats[prestationType] : []
  );

  const selectedFormat = order?.format || "Choisir un format";
  const selectedFormatTagId = order?.formatTagId;
  const currentFormatName = order?.format || "—";

  // Le format courant du devis correspond-il à un tag disponible (actif, ou
  // historique en mode classique) — par id, sinon par nom (anciens devis sans
  // formatTagId mais avec une string `format`) ?
  const matchedTag =
    (selectedFormatTagId ? availableFormatTags.find((t) => t.id === selectedFormatTagId) : null) ||
    (order?.format ? availableFormatTags.find((t) => t.name === order.format) : null);

  // Format introuvable dans les listes (ex. string legacy jamais seedée) :
  // on l'injecte comme option sélectionnée pour ne pas le perdre ni l'écraser.
  const showCurrentOption = !matchedTag && !!order?.format;
  const selectValue = matchedTag
    ? String(matchedTag.id)
    : (showCurrentOption ? "current" : "");

  const handleFormatSelect = (event) => {
    const value = event.target.value;
    // "current" = on garde le format historique du devis inchangé.
    if (value === "legacy" || value === "" || value === "current") {
      return;
    }
    const tagId = parseInt(value, 10);
    const tag = availableFormatTags.find((t) => t.id === tagId);
    if (tag) {
      dispatch(setFormat(orderIndex, tag.name, tag));
    }
  };

  const handleLegacyFormatSelect = (event) => {
    const format = event.target.value;
    dispatch(setFormat(orderIndex, format, null));
  };

  return (
    <>
      {formatTags.length > 0 ? (
        <Form.Group controlId="formatSelect">
          <Form.Label>
            Format* : <strong>{currentFormatName}</strong>
            {prestationType && (
              <span className="text-muted ml-2" style={{ fontWeight: "normal" }}>
                — type de prestation déduit : <strong>{toFrenchType(prestationType)}</strong>
              </span>
            )}
          </Form.Label>
          <FormControl as="select" value={selectValue} onChange={handleFormatSelect}>
            <option value="">Choisir un format</option>
            {showCurrentOption && (
              <option value="current">{currentFormatName} (format actuel)</option>
            )}
            {formatTags.map((tag) => (
              <option key={tag.id} value={tag.id}>
                {tag.name}{tag.prestation_type ? ` (${toFrenchType(tag.prestation_type)})` : ""}
              </option>
            ))}
            {!briqueMode && inactiveFormatTags.length > 0 && (
              <optgroup label="Formats historiques (mode classique)">
                {inactiveFormatTags.map((tag) => (
                  <option key={tag.id} value={tag.id}>
                    {tag.name}{tag.prestation_type ? ` (${toFrenchType(tag.prestation_type)})` : ""}
                  </option>
                ))}
              </optgroup>
            )}
          </FormControl>
        </Form.Group>
      ) : (
        <Form.Group controlId="formatSelectLegacy">
          <Form.Label>
            Format* ({toFrenchType(prestationType)}): <strong>{currentFormatName}</strong>
          </Form.Label>
          <FormControl as="select" value={selectedFormat} onChange={handleLegacyFormatSelect}>
            <option disabled value="Choisir un format">Choisir un format</option>
            {legacyFormats.map((format, key) => (
              <option key={key} value={format}>
                {format}
              </option>
            ))}
          </FormControl>
        </Form.Group>
      )}
    </>
  );
};

export default FormatSelectComponent;